<template>
  <div class="main-container">
    <div class="row py-3 mx-0">
      <div
        class="col-md-9 col-sm-6 d-flex justify-content-start"
        :class="{
          'flex-column': $breakpoint.smAndDown,
          'order-2': $breakpoint.xsOnly,
        }"
      >
        <label for="start-time" class="mr-2 mt-2">Offers available from <em>(24hr format)</em></label>
        <div class="d-flex flex-column">
          <div class="d-flex justify-content-start flex-row flex-wrap">
            <input
              class="pick-time-input"
              id="start-time"
              :style="[$breakpoint.xsOnly ? { width: 'calc(50% - 20px)' } : {}]"
              type="time"
              Min="01:00"
              Max="24:00"
              placeholder="--:--"
              v-model="startTime"
            />
            <strong class="mx-2 mt-2">to</strong>
            <input
              class="pick-time-input mr-2"
              :style="[$breakpoint.xsOnly ? { width: 'calc(50% - 20px)' } : {}]"
              type="time"
              Min="01:00"
              Max="24:00"
              placeholder="--:--"
              v-model="endTime"
            />
            <button
              class="btn btn-actionable time-availability"
              :class="[$breakpoint.smAndDown ? 'mt-2' : 'ml-2']"
              @click="confirmTimeAvailability"
              :disabled="invalidTimesFound"
            >
              Confirm availability
            </button>
          </div>
          <span v-if="invalidTimesFound" class="error-message mt-2">
            {{ invalidTimesFound }}
          </span>
        </div>
      </div>
      <div
        class="col-md-3 col-sm-4 offset-sm-2 offset-md-0"
        :class="[{ 'mb-2 order-1': $breakpoint.xsOnly }, { 'd-flex justify-content-end': $breakpoint.lgAndUp }]"
      >
        <button
          @click="$store.dispatch('livePrices/editSettlementPrices')"
          class="btn btn-actionable"
          :class="$breakpoint.mdAndDown ? 'my-1' : 'mr-2'"
        >
          {{ settlementPricesButtonText }}
        </button>
        <button class="btn btn-confirm" :disabled="!(getPrices.length > 0)" @click="openSpotModal()">
          Create Offer
        </button>
      </div>
    </div>
    <!-- title and options -->
    <div class="row mx-0 pb-1">
      <div class="col-md-6">
        <h1 class="first-header heading-medium mt-4" :class="{ 'mb-0': spotOffers.length < 1 }">
          <i class="fas fa-chart-line mr-2"></i>My Offers
        </h1>
      </div>
      <div class="col-md-6" v-if="spotOffers.length > 0">
        <div class="float-right d-flex">
          <strong>Show All My Offers</strong>
          <label class="switch selected">
            <input
              type="checkbox"
              class="on-off-toggler"
              id="master"
              v-model="toggleAllOffers"
              :disabled="disabledAllOffers || getPrices.length < 1"
              @click="switchAllOffersStatus()"
            />
            <span class="slider round lg"></span>
          </label>
          <strong>{{ toggleAllOffers ? "On" : "Off" }}</strong>
        </div>
      </div>
    </div>

    <div v-if="getPrices.length < 1" class="row mx-0 pb-3">
      <div class="col-md-12 px-0" :class="{ 'no-results d-flex': $breakpoint.mdAndDown }">
        <div class="w-100 alert tables-alert-custom-message-notify mt-3 text-center">
          <i class="fas fa-info-circle mr-2"></i>
          Offers will not be displayed to buyers until Settlement Prices have been updated. Click on
          {{ settlementPricesButtonText }} to update Settlement Prices
        </div>
      </div>
    </div>

    <div class="row mx-0">
      <div class="col-md-12" :class="{ 'px-0': !$breakpoint.mdAndDown }">
        <div class="table-container">
          <div v-if="spotOffers.length < 1" class="alert tables-alert-custom-message-notify mt-3 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            You have not created any offers for this market
          </div>
          <table v-else class="table responsive-table">
            <thead>
              <tr>
                <th v-for="(item, key) in tableTitles" :key="key">
                  {{ item.title }}
                  <i
                    v-if="item.sortKey === 'premium' && isPricingEngineEnabled"
                    class="fas fa-info-circle fa-lg ml-2"
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title="Premium (ppl) is equal to the sum of all entered Cost Addition, Bio and Temperature Rules for each offer. This does not include any additional Customer Margins."
                  ></i>
                  <span v-if="item.sortKey">
                    <span
                      v-if="item.sortKey === 'fuel' || item.sortKey === 'terminal' || item.sortKey === 'pricing_type'"
                    >
                      <i
                        v-if="activeSortColumn == item.sortKey && activeSortDirection == 'asc'"
                        class="fas fa-sort-alpha-down sort-icon"
                        :class="{ selected: activeSortColumn == item.sortKey && activeSortDirection == 'asc' }"
                        size="lg"
                        @click="sortColumn(item.sortKey, 'desc')"
                      ></i>
                      <i
                        v-else
                        class="fas sort-icon"
                        :class="
                          activeSortColumn == item.sortKey && activeSortDirection == 'desc'
                            ? ['selected', 'fa-sort-alpha-up']
                            : 'fa-sort-alpha-down'
                        "
                        size="lg"
                        @click="sortColumn(item.sortKey, 'asc')"
                      ></i>
                    </span>
                    <span v-else>
                      <i
                        v-if="activeSortColumn == item.sortKey && activeSortDirection == 'asc'"
                        class="fas fa-sort-numeric-down sort-icon"
                        :class="{ selected: activeSortColumn == item.sortKey && activeSortDirection == 'asc' }"
                        size="lg"
                        @click="sortColumn(item.sortKey, 'desc')"
                      ></i>
                      <i
                        v-else
                        class="fas sort-icon"
                        :class="
                          activeSortColumn == item.sortKey && activeSortDirection == 'desc'
                            ? ['selected', 'fa-sort-numeric-up']
                            : 'fa-sort-numeric-down'
                        "
                        size="lg"
                        @click="sortColumn(item.sortKey, 'asc')"
                      ></i>
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(offer, key) in spotOffers"
                :key="key"
                class="offer-rows"
                :class="{
                  disabled: getPrices.length < 1,
                  'pt-2 pb-4': $breakpoint.mdAndDown,
                }"
              >
                <td label="Product" v-if="offer.storage_type_id !== 'BUNKERING'">
                  <span>{{ offer.fuel_id | fueltype }}</span>
                </td>
                <td label="Product" v-else>
                  <span>Network</span>
                </td>
                <td label="Measurement">
                  <span>{{ offer.fuel_measurement_id | fuelmeasurementtype }}</span>
                </td>
                <td label="Terminal" v-if="offer.storage_type_id !== 'BUNKERING'">
                  <span>{{ offer.terminal_id | terminalsname }} </span>
                </td>
                <td label="Network Type" v-else>
                  <span>{{ networks.find((item) => item._id === offer.networkId).name }}</span>
                </td>
                <td label="Vol Min/L">
                  <span>{{ offer.minimumOrderQuantity | volumeformat }}</span>
                </td>
                <td label="Vol Max/L">
                  <span>{{ offer.maximumAvailable | volumeformat }}</span>
                </td>
                <td label="Bio Pricing">
                  <span>{{ offer.fuel_id | offerbiopricing }}</span>
                </td>
                <td label="Pricing Method">
                  <span>{{ offer.type_id | offertype }}</span>
                </td>
                <td label="Lifting Period">
                  <span v-if="offer.storage_type_id !== 'BUNKERING'">{{ offer | liftingperiodformat }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Premium (ppl)">
                  <span v-if="offer.type_id !== 'FIXED'">{{ offer.premium | priceformat }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Price (ppl)">
                  <span v-if="offer.type_id === 'SETTLEMENT'">Pending Settlement</span>
                  <span v-else-if="getPrices.length == 0 && offer.type_id !== 'FIXED'">N/A</span>
                  <span v-else>{{ offer.price | priceformat }}</span>
                  <i
                    v-if="getPrices.length > 0 && offer.type_id === 'TRACKING'"
                    class="fas fa-sm fa-rss ml-2"
                    :class="
                      toggleStates[key] && !isToggleDisabled(offer)
                        ? 'tracking-active-icon-color'
                        : 'tracking-inactive-icon-color'
                    "
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title="This Offer Price updates every 30 seconds"
                  ></i>
                </td>
                <td class="td-actions">
                  <button
                    class="btn btn-icon btn-icon-color td-sticky td-left-bottom"
                    :class="{ 'pt-3': $breakpoint.mdAndDown }"
                    :disabled="getPrices.length < 1"
                    @click="assignOfferToDuplicate(offer._id)"
                  >
                    <i class="fas fa-clone fa-lg"></i>
                  </button>
                </td>
                <td class="td-actions">
                  <button
                    class="btn btn-icon btn-icon-color td-sticky td-center-left-bottom"
                    :class="{ 'pt-3': $breakpoint.mdAndDown }"
                    @click="editOfferMethod(offer._id)"
                  >
                    <i class="fas fa-edit fa-lg"></i>
                  </button>
                </td>
                <td class="td-actions">
                  <div class="pt-2" v-if="$breakpoint.mdAndDown"></div>
                  <span class="switch-value"></span>
                  <label class="switch selected td-sticky td-center-right-bottom">
                    <input
                      type="checkbox"
                      class="on-off-toggler"
                      v-model="toggleStates[key]"
                      @click="switchSingleToggle(key)"
                      :disabled="isToggleDisabled(offer)"
                      :key="offer._id"
                    />
                    <span class="slider round" :class="{ disabledtoggle: disabledAllOffers === true }"></span>
                  </label>
                  <span class="switch-value"></span>
                </td>
                <td class="td-actions">
                  <button
                    class="btn btn-icon td-sticky td-right-bottom"
                    :class="{ 'pt-3': $breakpoint.mdAndDown }"
                    @click="removeOffer(offer)"
                  >
                    <i class="fas fa-trash-alt fa-lg"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import breakpoint from "@/mixins/index";

export default {
  mixins: [breakpoint],
  data: function () {
    return {
      tableTitles: [
        { title: "Product", sortKey: "fuel" },
        { title: "Measurement", sortKey: "fuel_measurement_id" },
        { title: "Terminal", sortKey: "terminal" },
        { title: "Vol Min/L", sortKey: "volume_min" },
        { title: "Vol Max/L", sortKey: "volume_max" },
        { title: "Bio Pricing", sortKey: "bio_pricing" },
        { title: "Pricing Method", sortKey: "pricing_type" },
        { title: "Lifting Period", sortKey: "collection_days" },
        { title: "Premium (ppl)", sortKey: "premium" },
        { title: "Price (ppl)", sortKey: "price" },
        { title: "Duplicate" },
        { title: "Edit" },
        { title: "Off/On" },
        { title: "Remove" },
      ],
      toggleAllOffers: false,
      startTime: "",
      endTime: "",
      activeSortDirection: "",
      activeSortColumn: "",
    };
  },
  created() {
    this.getDinTerminals();
    this.setAvailabilityDetails();
  },
  updated() {
    $('[data-toggle="tooltip"]').tooltip();
  },
  methods: {
    ...mapActions("itemRemoval", ["openConfirmationRemoval"]),
    ...mapActions("sellerOffers", [
      "getAllOffersForUser",
      "openSpotModal",
      "editOffer",
      "switchNewOffersToFalse",
      "saveOffersOnTheStore",
      "resetSuccessEditing",
      "getOffersTimeAvailability",
      "setOffersTimeAvailability",
      "toggleOffersAvailability",
      "getDinTerminals",
      "duplicateOffer",
    ]),
    assignOfferToDuplicate: function (offerId) {
      const payload = {
        offerId: offerId,
        offerType: "SPOT",
      };

      this.duplicateOffer(payload);
    },
    editOfferMethod: function (offerId) {
      const payload = {
        offerId: offerId,
        offerType: "SPOT",
      };

      this.editOffer(payload);
    },
    isToggleDisabled: function (offer) {
      const fuel = this.getSpotFuels.find((spotFuel) => spotFuel._id === offer.fuel_id);
      if (
        (offer.terminal && offer.terminal.status && offer.terminal.status !== "100") || // Is terminal valid?
        this.disabledAllOffers || // Is the master toggle switched off?
        (offer.type_id !== "SETTLEMENT" && this.getPrices.length < 1) || // Have sellers entered daily settlement prices? (not required for TAS)
        !this.toggleAllOffers || // Is spot enabled for this seller?
        offer.minimumOrderQuantity > offer.maximumAvailable || // Is there still enough volume to buy this offer?
        (!fuel && !this.blendedProductConfig.includes(offer.fuel_id))
      ) {
        //Fuel has been disabled for this seller, we cannot disable blended products
        return true;
      }

      return false;
    },
    switchSingleToggle: async function (key) {
      const offerToModify = this.spotOffers[key];
      let editTo = offerToModify.status_type_id === "ACTIVE" ? "PAUSED" : "ACTIVE";

      const response = await this.axios.put("/api/editOffer", {
        params: {
          offerId: offerToModify._id,
          userId: this.userLoggedIn.userId,
          statusTypeId: editTo,
        },
      });

      if (response.data.success) {
        this.getAllOffersForUser();
      } else {
        console.log("Error found", response);
      }
    },
    switchAllOffersStatus: function () {
      const payload = {
        type: "SPOT",
        newStatus: !this.offersAvailability.spot.enabled,
      };

      this.toggleOffersAvailability(payload);
    },
    setAvailabilityDetails: function () {
      this.startTime = this.offersAvailability.spot.start;
      this.endTime = this.offersAvailability.spot.end;

      this.toggleAllOffers = this.offersAvailability.spot.enabled;
    },
    confirmTimeAvailability: function () {
      const payload = {
        type: "SPOT",
        newStartTime: this.startTime,
        newEndTime: this.endTime,
      };

      this.setOffersTimeAvailability(payload);
    },
    removeOffer: function (offer) {
      const payload = {
        item: offer,
        type: "OFFER",
        showWindow: true,
      };
      this.openConfirmationRemoval(payload);
    },
    sortColumn(sortKey, direction) {
      let stringA = "";
      let stringB = "";

      this.activeSortDirection = direction;
      this.activeSortColumn = sortKey;

      switch (sortKey) {
        case "fuel": // Sort by fuel type
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.storage_type_id == "SPOT" ? this.$root.$options.filters.fueltype(a.fuel_id) : "Network";
            stringB = b.storage_type_id == "SPOT" ? this.$root.$options.filters.fueltype(b.fuel_id) : "Network";

            if (direction == "asc") {
              if (stringA < stringB) {
                return -1;
              }
              if (stringA > stringB) {
                return 1;
              }
              return 0;
            } else {
              if (stringA < stringB) {
                return 1;
              }
              if (stringA > stringB) {
                return -1;
              }
              return 0;
            }
          });
        case "bio_pricing": // Sort by bio pricing
          return this.liveOffersSeller.sort((a, b) => {
            stringA = this.$root.$options.filters.offerbiopricing(a.fuel_id);
            stringB = this.$root.$options.filters.offerbiopricing(b.fuel_id);

            if (direction == "asc") {
              if (stringA < stringB) {
                return -1;
              }
              if (stringA > stringB) {
                return 1;
              }
              return 0;
            } else {
              if (stringA < stringB) {
                return 1;
              }
              if (stringA > stringB) {
                return -1;
              }
              return 0;
            }
          });
        case "terminal": // Sort by terminal
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.storage_type_id == "SPOT" ? a.terminal.name : this.getNetworkName(a.networkId);
            stringB = b.storage_type_id == "SPOT" ? b.terminal.name : this.getNetworkName(b.networkId);

            if (direction == "asc") {
              if (stringA < stringB) {
                return -1;
              }
              if (stringA > stringB) {
                return 1;
              }
              return 0;
            } else {
              if (stringA < stringB) {
                return 1;
              }
              if (stringA > stringB) {
                return -1;
              }
              return 0;
            }
          });
        case "volume_min": // Sort by minimum volume
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.minimumOrderQuantity;
            stringB = b.minimumOrderQuantity;

            if (direction == "asc") {
              return stringA - stringB;
            } else {
              return stringB - stringA;
            }
          });
        case "volume_max": // Sort by maximum volume
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.maximumAvailable;
            stringB = b.maximumAvailable;

            if (direction == "asc") {
              return stringA - stringB;
            } else {
              return stringB - stringA;
            }
          });
        case "collection_days": // Sort by Lifting Period
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.storage_type_id == "SPOT" ? a.collectionTime : 100;
            stringB = b.storage_type_id == "SPOT" ? b.collectionTime : 100;

            if (direction == "asc") {
              return stringA - stringB;
            } else {
              return stringB - stringA;
            }
          });
        case "pricing_type": // Sort by pricing type
          return this.liveOffersSeller.sort((a, b) => {
            stringA = this.$root.$options.filters.offertype(a.type_id);
            stringB = this.$root.$options.filters.offertype(b.type_id);

            if (direction == "asc") {
              if (stringA < stringB) {
                return -1;
              }
              if (stringA > stringB) {
                return 1;
              }
              return 0;
            } else {
              if (stringA < stringB) {
                return 1;
              }
              if (stringA > stringB) {
                return -1;
              }
              return 0;
            }
          });
        case "premium": // Sort by premium
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.type_id == "FIXED" ? 0 : a.price;
            stringB = b.type_id == "FIXED" ? 0 : b.price;

            if (direction == "asc") {
              return stringA - stringB;
            } else {
              return stringB - stringA;
            }
          });
        case "price": // Sort by live price
          return this.liveOffersSeller.sort((a, b) => {
            stringA = a.type_id == "FIXED" ? a.price : a.livePrice;
            stringB = b.type_id == "FIXED" ? b.price : b.livePrice;

            if (direction == "asc") {
              return stringA - stringB;
            } else {
              return stringB - stringA;
            }
          });
        default:
          return;
      }
    },
    getNetworkName: function (networkId) {
      return this.networks.find((n) => n._id === networkId).name;
    },
  },
  computed: {
    ...mapState("itemRemoval", ["showItemRemovalConfirmation"]),
    ...mapState("sellerOffers", [
      "liveOffersSeller",
      "showSpotModal",
      "newOffers",
      "editingOffer",
      "filteredOffers",
      "filtersApply",
      "newOfferBeenAdded",
      "editWasSuccess",
      "offersAvailability",
    ]),
    ...mapState("login", ["userLoggedIn"]),
    ...mapGetters("livePrices", ["getPrices", "getPricesUpdate", "settlementPricesButtonText"]),
    ...mapGetters("login", ["isPricingEngineEnabled"]),
    ...mapGetters("metadata", ["networks"]),
    ...mapGetters("fuelsType", ["getSpotFuels"]),
    ...mapGetters("metadata", ["blendedProductConfig"]),
    spotOffers: {
      get: function () {
        let offers = [];

        if (this.liveOffersSeller) {
          this.sortColumn(this.activeSortColumn, this.activeSortDirection);
          offers = this.liveOffersSeller.filter((offer) => !offer.isContract);
        }
        return offers;
      },
    },
    toggleStates: {
      get: function () {
        let states = [];

        if (this.spotOffers) {
          this.spotOffers.forEach((offer) => {
            if (offer.status_type_id == "ACTIVE") {
              states.push(true);
            } else {
              states.push(false);
            }
          });
        }

        return states;
      },
    },
    disabledAllOffers: {
      get: function () {
        let status;
        const currentTime = new Date();

        if (this.offersAvailability) {
          const startingHour = this.offersAvailability.spot.start.substring(0, 2);
          const startingMinute = this.offersAvailability.spot.start.substring(3);
          const startTime = new Date().setHours(startingHour, startingMinute);

          const endHour = this.offersAvailability.spot.end.substring(0, 2);
          const endMinute = this.offersAvailability.spot.end.substring(3);
          const endTime = new Date().setHours(endHour, endMinute);

          if (currentTime < startTime || endTime <= currentTime) {
            status = true;
          } else {
            status = false;
          }
        }

        return status;
      },
    },
    invalidTimesFound: {
      get: function () {
        const re = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;

        if (this.startTime && this.endTime) {
          if (!this.startTime.match(re) || !this.endTime.match(re)) {
            return "Please enter a valid time format";
          } else if (
            new Date().setHours(this.endTime.substring(0, 2), this.endTime.substring(3)) <=
            new Date().setHours(this.startTime.substring(0, 2), this.startTime.substring(3))
          ) {
            return "Closing time must be later than opening time";
          }
        } else {
          return "Please enter a valid time range";
        }
      },
    },
    triggerOfferModal() {
      if (this.showSpotModal) {
        let element = this.$root.$refs.offerModal.$el;
        $(element).modal({ backdrop: "static", keyboard: false });
      }
      return;
    },
    triggerItemRemovalModal() {
      if (this.showItemRemovalConfirmation) {
        let element = this.$root.$refs.confirmationRemoveItem.$el;
        $(element).modal({ backdrop: "static", keyboard: false });
      }
      return;
    },
  },
  watch: {
    triggerOfferModal() {},
    triggerItemRemovalModal() {},
    disableEnableTogglesDependingOnTime() {},
  },
};
</script>

<style lang="scss" scoped>
.fa-sliders-h {
  cursor: pointer;
}
.form-container {
  position: relative;
}
</style>
